import { AccessLevel } from './access-level.model';
import { BaseApi } from './base-api.model';
import { DocumentAttribute } from './document.model';

/**
 * Owner side of a document reference. Sent as `entityType` on upload and
 * used to pick which `*DocumentReference` table/store to talk to. Also
 * stamped client-side onto every fetched/uploaded record (see
 * `DocumentReferenceBase`) even though the backend response itself doesn't
 * include it.
 */
export enum DocumentReferenceEntityType {
  CHAT = 'CHAT',
  COMPANY = 'COMPANY',
  ITEM = 'ITEM',
  NDA_TEMPLATE = 'NDA_TEMPLATE',
  PERSON = 'PERSON',
  TEMPLATE_DATA = 'TEMPLATE_DATA',
}

export type DocumentReferenceTableName =
  | 'ChatDocumentReference'
  | 'CompanyDocumentReference'
  | 'ItemDocumentReference'
  | 'NdaTemplateDocumentReference'
  | 'PersonDocumentReference'
  | 'TemplateDataDocumentReference';

/**
 * `entityType` -> backend model/table name (`/api/v2/model/<name>`, and the
 * WS entity type is the same string). Kept as an explicit map rather than a
 * string transform so an unexpected backend naming exception fails loudly
 * (missing map entry) instead of silently building a wrong URL.
 */
export const DOCUMENT_REFERENCE_TABLE_NAME: Record<
  DocumentReferenceEntityType,
  DocumentReferenceTableName
> = {
  [DocumentReferenceEntityType.CHAT]: 'ChatDocumentReference',
  [DocumentReferenceEntityType.COMPANY]: 'CompanyDocumentReference',
  [DocumentReferenceEntityType.ITEM]: 'ItemDocumentReference',
  [DocumentReferenceEntityType.NDA_TEMPLATE]: 'NdaTemplateDocumentReference',
  [DocumentReferenceEntityType.PERSON]: 'PersonDocumentReference',
  [DocumentReferenceEntityType.TEMPLATE_DATA]: 'TemplateDataDocumentReference',
};

/**
 * Composite id used by TemplateData's `templateDataId` FK
 * (`VersionedEntityId` in the OpenAPI spec) — templates are versioned, so
 * the reference pins both the entity id and the version it was attached at.
 */
export interface VersionedEntityId {
  id: string;
  version: string;
}

/**
 * Category of legal/business document content (`document.documentType` in
 * the OpenAPI spec). Distinct from both the legacy numeric `OldDocumentAttribute`
 * (the upload "slot", e.g. `COMPANY_LOGO = 1000`) and `DocumentAttribute`
 * (the upload "purpose" string) — this is a third, separate axis describing
 * what kind of content the file actually is.
 */
export enum DocumentType {
  NDA = 'NDA',
  TERM_SHEET = 'TERM_SHEET',
  CREDIT_AGREEMENT = 'CREDIT_AGREEMENT',
  FINANCIALS = 'FINANCIALS',
  AUDIT = 'AUDIT',
  COMPLIANCE_CERTIFICATE = 'COMPLIANCE_CERTIFICATE',
  BUDGET = 'BUDGET',
  LOGO = 'LOGO',
  LOAN_AGREEMENT = 'LOAN_AGREEMENT',
  LOAN_NOTE = 'LOAN_NOTE',
  SYNDICATION_AGREEMENT = 'SYNDICATION_AGREEMENT',
  TRANSFER_CERTIFICATE = 'TRANSFER_CERTIFICATE',
  PARTICIPATION_AGREEMENT = 'PARTICIPATION_AGREEMENT',
  AGENCY_AGREEMENT = 'AGENCY_AGREEMENT',
  UNKNOWN = 'UNKNOWN',
}

export enum DocumentRetryPolicy {
  IMMEDIATE = 'IMMEDIATE',
  NORMAL = 'NORMAL',
  RELAXED = 'RELAXED',
}

export enum VirusScanStatus {
  PENDING = 'PENDING',
  OK = 'OK',
  CONTAMINATED = 'CONTAMINATED',
  FAILED = 'FAILED',
}

/** `document.status` — server-side lifecycle of the file behind a reference. */
export enum DocumentStatus {
  UPLOAD_PENDING = 'UPLOAD_PENDING',
  UPLOADED = 'UPLOADED',
  SYSTEM_GENERATED = 'SYSTEM_GENERATED',
  PLACEHOLDER = 'PLACEHOLDER',
  MISSING_DOC = 'MISSING_DOC',
}

/**
 * The `extension` values the serving endpoint accepts
 * (`DocumentServeController.java`) — a thumbnail, a PDF preview render, or
 * the extracted-text projection. Omit for the original file.
 */
export type DocumentExtension = 'sm-thb' | 'md-thb' | 'prv' | 'txt';

/**
 * The `document` field nested in every `*DocumentReferenceApi` response
 * (`DocumentApi` in the OpenAPI spec) — the actual file, as opposed to the
 * reference record pointing at it. Confirmed against the live schema; it's
 * a reduced DTO projection of the domain `Document` (no `bucket`,
 * `newExtensions`, `documentMetaList`, or `execFailed` — those exist only on
 * the internal domain model, not over the wire).
 */
export interface ReferencedDocument extends BaseApi {
  accessLevel: AccessLevel;
  contentType: string;
  storageKey: string;
  description?: string;
  documentType: DocumentType;
  originalFileName: string;
  size: number;
  extensions?: DocumentExtension[];
  executorPlan?: string[];
  executorDone?: string[];
  executorFailed?: string[];
  status: DocumentStatus;
  virusScanStatus: VirusScanStatus;
  retryPolicy: DocumentRetryPolicy;
  // Only for token request
  categoryId?: string;
}

/**
 * Fields shared by all 6 `*DocumentReferenceApi` responses — confirmed
 * identical across `CompanyDocumentReferenceApi`, `PersonDocumentReferenceApi`,
 * `ItemDocumentReferenceApi`, `ChatDocumentReferenceApi`,
 * `TemplateDataDocumentReferenceApi`, `NdaTemplateDocumentReferenceApi` in
 * the live OpenAPI spec. Every entity-specific interface below extends this
 * and adds only its own FK field(s).
 *
 * `entityType` is NOT part of the backend response — none of the 6 schemas
 * include it, since which entity a record belongs to is implicit in which
 * endpoint/table you queried. It's stamped on client-side by the store
 * layer (`withDocumentReferenceMethods`, which already knows its own
 * `entityType`) so consumers can narrow the `DocumentReference` union
 * without separately tracking "which store did this come from".
 */
export interface DocumentReferenceBase extends BaseApi {
  entityId: string;
  document: ReferencedDocument;
  attribute: DocumentAttribute;
  description?: string;
  userIdForToken?: string;
  latestToken?: string;
}

export interface CompanyDocumentReference extends DocumentReferenceBase {
  entityType: DocumentReferenceEntityType.COMPANY;
}

export interface PersonDocumentReference extends DocumentReferenceBase {
  entityType: DocumentReferenceEntityType.PERSON;
}

/**
 * `DocumentAccess.type` — the libv2 port of the v1 `Docacsapi.Type` integer codes
 * (`UNKNOWN=0, PREVIEW=10, PLACEHOLDER_CREATION=20, UPLOAD=30,
 * SINGLE_FILE_DOWNLOAD=50, BULK_DOWNLOAD=100`), now a string enum over the wire.
 */
export enum DocumentAccessType {
  UNKNOWN = 'UNKNOWN',
  PREVIEW = 'PREVIEW',
  PLACEHOLDER_CREATION = 'PLACEHOLDER_CREATION',
  UPLOAD = 'UPLOAD',
  SINGLE_FILE_DOWNLOAD = 'SINGLE_FILE_DOWNLOAD',
  BULK_DOWNLOAD = 'BULK_DOWNLOAD',
}

/**
 * `DocumentAccess.status` — the libv2 port of the v1 `Docacsapi.Status` integer
 * codes (`UNKNOWN=0, OK=1, FAILURE=999`), now a string enum over the wire.
 */
export enum DocumentAccessStatus {
  UNKNOWN = 'UNKNOWN',
  OK = 'OK',
  FAILURE = 'FAILURE',
}

/**
 * One access event for a document reference (who touched it, when, how) —
 * only populated when the reference is fetched with `?view=access`
 * (`ItemDocumentReferenceEntityTask#accessView`, replaces the v1 `docacs`
 * query). `accessDate` is **not ISO-8601** — the backend serializes it as
 * `yyyy-MM-dd HH:mm:ss.SSSSSS xxx` (see `DocumentAccess.java`'s
 * `@JsonFormat`), so callers must normalize it before parsing.
 */
export interface DocumentAccess {
  userId: string;
  accessDate: string;
  type: DocumentAccessType;
  status: DocumentAccessStatus;
  errorMessage?: string;
  email?: string;
  companyName?: string;
  firstName?: string;
  lastName?: string;
}

export interface ItemDocumentReference extends DocumentReferenceBase {
  entityType: DocumentReferenceEntityType.ITEM;
  categoryId?: string;
  workflowId?: string;
  prerequisiteId?: string;
  /** Only present when fetched with `?view=access` — see {@link DocumentAccess}. */
  documentAccessList?: DocumentAccess[];
}

export interface ChatDocumentReference extends DocumentReferenceBase {
  entityType: DocumentReferenceEntityType.CHAT;
}

export interface TemplateDataDocumentReference extends DocumentReferenceBase {
  entityType: DocumentReferenceEntityType.TEMPLATE_DATA;
}

export interface NdaTemplateDocumentReference extends DocumentReferenceBase {
  entityType: DocumentReferenceEntityType.NDA_TEMPLATE;
}

/**
 * Discriminated union of every `*DocumentReferenceApi` response shape —
 * narrow on `entityType` to get the entity-specific FK field.
 */
export type DocumentReference =
  | CompanyDocumentReference
  | PersonDocumentReference
  | ItemDocumentReference
  | ChatDocumentReference
  | TemplateDataDocumentReference
  | NdaTemplateDocumentReference;

export interface DocumentReferenceUploadPayload {
  file: Blob;
  fileName: string;
  entityType: DocumentReferenceEntityType;
  /** Existing parent to attach to. Omit when creating one on demand via `entityJson`. */
  entityId?: string;
  /** Defaults per entity type on the backend when omitted. */
  attribute?: DocumentAttribute;
  accessLevel: AccessLevel;
  description?: string;
  /** Folder to file the upload into (items only). `undefined` = root. */
  categoryId?: string;
  /**
   * The parent entity's `*Api` JSON, to create it as part of the upload. Mutually
   * exclusive with `entityId` — the v2 replacement for v1's
   * `_ks: '<entity>={{<<crud-insert>>}}'`.
   */
  entityJson?: string;
  /**
   * Content classification to drive extraction (e.g. `TERM_SHEET` triggers
   * `TermSheetExtractor`), for a caller that already knows it — the deal steppers,
   * where the user chose "term sheet" or "credit agreement" on the way in. Accepted
   * by `upload-tokenless` as of synply-api!1977; omit it to let the backend's
   * `DocumentClassifier` settle the type post-upload instead. Declaring it means the
   * classifier leaves the document alone (SD-2745).
   */
  documentType?: DocumentType;
}
